import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Grid,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Input,
  FormHelperText,
  FormControlLabel,
  Checkbox,
  IconButton,
  TextField,
  Box,
  Select,
  MenuItem
} from '@material-ui/core';
import DialogTitle from '@material-ui/core/DialogTitle';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import VideoCallIcon from '@material-ui/icons/VideoCall';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { ThemeProvider, createTheme, makeStyles } from '@material-ui/core/styles';
import { DropzoneArea } from 'material-ui-dropzone';
import Validator from 'validator';

import { getBanners, editBanner } from 'redux/ducks/banner';
import { fetchProductsGeneral } from 'redux/ducks/stock';
import { Banner, BannerSlides } from 'interfaces/banner';
import Loading from 'components/Common/Loading';
import IStore from 'interfaces/store';

const BANNER_CONTAINER_ID = 2;

const useStyles = makeStyles({
  activeButton: {
    margin: '0 5px',
    color: '#fff',
    border: 'none',
    borderRadius: 0,
    backgroundColor: 'black',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    '&:hover': {
      color: '#fe309c',
      backgroundColor: 'black'
    }
  }
});

const dropzoneTheme = (createTheme as any)({
  overrides: {
    MuiDropzoneArea: {
      root: { minHeight: 'auto', maxWidth: '25vw' },
      text: { fontSize: '1rem' },
      icon: { width: '33px' }
    },
    MuiDropzonePreviewList: {
      root: { justifyContent: 'center', position: 'absolute', top: 0 },
      imageContainer: { maxWidth: '100%', flexBasis: '100%' }
    }
  }
});

export default function BannerSlidesPage() {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { stock, banner: bannerState } = useSelector<IStore, IStore>((state) => state);

  const [slides, setSlides] = useState<any[]>([]);
  const [bannerId, setBannerId] = useState<number>(0);
  const [placementTitle, setPlacementTitle] = useState('');
  const [placementTitleError, setPlacementTitleError] = useState('');
  const [placementDesc, setPlacementDesc] = useState('');
  const [placementDescError, setPlacementDescError] = useState('');
  const [allEditions, setAllEditions] = useState<{ id: number; productName: string; value: number }[]>([]);
  const [inputValue, setInputValue] = useState<string[]>([]);
  const [product, setProduct] = useState<{ id: number; productName: string; value: number }[]>([]);
  const [productError, setProductError] = useState<string[]>([]);
  const [slidesError, setSlidesError] = useState<string[][]>([['', '', '', '', '']]);
  const [imagePaths, setImagePaths] = useState<string[]>([]);
  const [previewImage, setPreviewImage] = useState<boolean[]>([]);
  const [imagePathsPortrait, setImagePathsPortrait] = useState<string[]>([]);
  const [previewImagePortrait, setPreviewImagePortrait] = useState<boolean[]>([]);
  const [previewVideo, setPreviewVideo] = useState<boolean[]>([]);
  const [clicked, setClicked] = useState(false);
  const [deletedSlides, setDeletedSlides] = useState<any[]>([]);
  const [orderedSlides, setOrderedSlides] = useState<{ sortKey: number }[]>([]);

  const loadBanner = async () => {
    const banners: Banner[] = await dispatch(getBanners());
    const container = banners.find((b) => b.id === BANNER_CONTAINER_ID);
    if (container) {
      setBannerId(container.id);
      setPlacementTitle(container.placement_title || '');
      setPlacementDesc(container.placement_description || '');
      const sorted = [...(container.banner_slides || [])].sort((a, b) => a.sort_key - b.sort_key);
      setSlides(sorted);

      const q = sorted.map((s) => s.image as string);
      const qz = sorted.map((s) => s.image_portrait as string);
      const z = new Array(sorted.length).fill(false);
      const zx = sorted.map((s) => (s.internal_product as any)?.name || '');
      const prods = sorted.map((s) => ({
        id: (s.internal_product as any)?.product_id || 0,
        productName: (s.internal_product as any)?.name || '',
        value: (s.internal_product as any)?.price || 0
      }));
      const errs = new Array(sorted.length).fill(['', '', '', '', '']);

      setImagePaths(q);
      setImagePathsPortrait(qz);
      setPreviewImage(z);
      setPreviewImagePortrait(z);
      setPreviewVideo(z);
      setInputValue(zx);
      setProduct(prods);
      setProductError(new Array(sorted.length).fill(''));
      setSlidesError(errs);
      setOrderedSlides(sorted.map((s) => ({ sortKey: s.sort_key })));
    }
  };

  useEffect(() => {
    loadBanner();
    if (!stock?.products?.length) dispatch(fetchProductsGeneral());
  }, []);

  useEffect(() => {
    if (stock?.products?.length > 0) {
      setAllEditions(
        stock.products.map((p) => ({
          id: p.product_id,
          productName: p.name,
          value: p.price
        }))
      );
    }
  }, [stock.products]);

  useEffect(() => {
    const control = document.querySelectorAll<HTMLElement>('.d-control');
    control.forEach((c) => { c.style.color = 'black'; });
    const icon = document.querySelectorAll<HTMLElement>('.d-control-icon');
    icon.forEach((c) => { c.style.color = '#BBBBBB'; });
    const d = document.querySelectorAll<HTMLElement>('.d-banner-slides');
    if (d.item(0)) {
      d.item(0).style.color = '#fe309c';
      if (d.item(1)) d.item(1).style.color = '#fe309c';
    }
    const dText = document.querySelectorAll<HTMLElement>('.d-banner-slides-text');
    if (dText.item(0)) dText.item(0).style.color = '#fe309c';
  });

  const getSlideSortKey = (index: number) => {
    if (orderedSlides.length > index && orderedSlides[index]) {
      return orderedSlides[index].sortKey;
    }
    return null;
  };

  const disableOrderSelect = (index: number) => index + 1 > slides.length;

  const handleOrderedSlides = (value: number, index: number) => {
    const updated = orderedSlides.map((s, i) => i === index ? { sortKey: value } : s);
    const updatedSlides = [...slides];
    updatedSlides[index] = { ...updatedSlides[index], sort_key: value };
    setOrderedSlides(updated);
    setSlides(updatedSlides);
  };

  const addSlide = () => {
    const newSlide: BannerSlides = {
      id: 0, image: '', image_portrait: '', video: '',
      external_url: '', description: '', hide_description: false,
      sort_key: slides.length + 1, internal_product: null
    };
    setSlides([...slides, newSlide]);
    setProduct([...product, { id: 0, productName: '', value: 0 }]);
    setProductError([...productError, '']);
    setSlidesError([...slidesError, ['', '', '', '', '']]);
    setImagePaths([...imagePaths, '']);
    setPreviewImage([...previewImage, false]);
    setImagePathsPortrait([...imagePathsPortrait, '']);
    setPreviewImagePortrait([...previewImagePortrait, false]);
    setPreviewVideo([...previewVideo, false]);
    setInputValue([...inputValue, '']);
    setOrderedSlides([...orderedSlides, { sortKey: slides.length + 1 }]);
  };

  const deleteSlideLocal = (index: number) => {
    const updated = [...slides];
    const deleted = [...deletedSlides];
    deleted.push(updated[index]);
    setDeletedSlides(deleted);
    updated.splice(index, 1);
    setSlides(updated);
    const p = [...product]; p.splice(index, 1); setProduct(p);
    const pe = [...productError]; pe.splice(index, 1); setProductError(pe);
    const se = [...slidesError]; se.splice(index, 1); setSlidesError(se);
    const ip = [...imagePaths]; ip.splice(index, 1); setImagePaths(ip);
    const pi = [...previewImage]; pi.splice(index, 1); setPreviewImage(pi);
    const ipp = [...imagePathsPortrait]; ipp.splice(index, 1); setImagePathsPortrait(ipp);
    const pip = [...previewImagePortrait]; pip.splice(index, 1); setPreviewImagePortrait(pip);
    const pv = [...previewVideo]; pv.splice(index, 1); setPreviewVideo(pv);
    const iv = [...inputValue]; iv.splice(index, 1); setInputValue(iv);
    const os = [...orderedSlides]; os.splice(index, 1); setOrderedSlides(os);
  };

  const handleSlideDesc = (value: string, index: number) => {
    const x = [...slides]; x[index].description = value; setSlides(x);
    const z = [...slidesError]; z[index][0] = ''; setSlidesError(z);
  };

  const handleSlideExternalURL = (value: string, index: number) => {
    const x = [...slides]; x[index].external_url = value; setSlides(x);
    const z = [...slidesError]; z[index][3] = ''; setSlidesError(z);
  };

  const handleHideDescription = (index: number) => {
    const x = [...slides]; x[index].hide_description = !x[index].hide_description; setSlides(x);
  };

  const handleImage = (event: any, index: number) => {
    if (event.length > 0) {
      const x = [...slides]; x[index].image = event[0]; setSlides(x);
      const z = [...slidesError]; z[index][1] = ''; setSlidesError(z);
      const w = [...imagePaths]; w[index] = URL.createObjectURL(event[0]); setImagePaths(w);
      const q = [...previewImage]; q[index] = true; setPreviewImage(q);
    }
  };

  const handleImagePortrait = (event: any, index: number) => {
    if (event.length > 0) {
      const x = [...slides]; x[index].image_portrait = event[0]; setSlides(x);
      const z = [...slidesError]; z[index][4] = ''; setSlidesError(z);
      const w = [...imagePathsPortrait]; w[index] = URL.createObjectURL(event[0]); setImagePathsPortrait(w);
      const q = [...previewImagePortrait]; q[index] = true; setPreviewImagePortrait(q);
    }
  };

  const handleVideo = (event: any, index: number) => {
    if (event.length > 0) {
      const x = [...slides]; x[index].video = event[0]; setSlides(x);
      const z = [...slidesError]; z[index][2] = ''; setSlidesError(z);
      const q = [...previewVideo]; q[index] = true; setPreviewVideo(q);
    }
  };

  const handleChangeProduct = (value: any, index: number) => {
    const x = [...product];
    const pe = [...productError];
    if (value !== null) {
      x[index] = value;
      pe[index] = '';
    } else {
      x[index] = { id: 0, productName: '', value: 0 };
    }
    setProduct(x);
    setProductError(pe);
  };

  const checkFormError = () => {
    let hasError = false;
    if (placementTitle === '') {
      setPlacementTitleError('Please enter placement title');
      hasError = true;
    }
    if (placementDesc === '') {
      setPlacementDescError('Please enter placement description');
      hasError = true;
    }
    slides.forEach((element, index) => {
      if (element.external_url && element.external_url !== '' && !Validator.isURL(element.external_url)) {
        const x = [...slidesError]; x[index][3] = 'Please enter a valid URL (http(s)://something.something)';
        setSlidesError(x); hasError = true;
      }
      if (product[index]?.productName !== null && product[index]?.productName === '' && (!element.external_url || element.external_url === '')) {
        const x = [...productError]; x[index] = 'Please enter a product'; setProductError(x); hasError = true;
      }
      if (product[index]?.productName && product[index]?.productName !== '' && element.external_url && element.external_url !== '') {
        const x = [...slidesError]; x[index][3] = 'You can either enter an external URL or select an artwork';
        setSlidesError(x); hasError = true;
      }
    });
    return hasError;
  };

  const handleSubmit = async () => {
    if (checkFormError()) return;
    setClicked(true);

    const newSlides = [];
    const oldSlides = [];

    slides.forEach((element, index) => {
      const base: any = {
        image: element.image,
        image_portrait: element.image_portrait,
        video: element.video,
        external_url: element.external_url ?? '',
        description: element.description,
        hide_description: element.hide_description,
        internal_product: product[index]?.id === 0 ? '' : product[index]?.id,
        sort_key: element.sort_key
      };
      try {
        if (typeof base.image === 'string' && base.image.includes('https://res.cloudinary.com/')) delete base.image;
        if (typeof base.image_portrait === 'string' && base.image_portrait.includes('https://res.cloudinary.com/')) delete base.image_portrait;
      } catch (e) { console.log(e); }
      if (element.external_url) base.internal_product = '';

      if (element.id === 0) {
        newSlides.push(base);
      } else {
        oldSlides.push({ ...base, id: element.id });
      }
    });

    const deletedSlidess = deletedSlides.filter((s) => s.id !== 0).map((s) => ({ id: s.id }));

    try {
      await dispatch(
        editBanner(
          bannerId,
          { placement_title: placementTitle, placement_description: placementDesc },
          newSlides,
          oldSlides,
          deletedSlidess as any
        )
      );
      setDeletedSlides([]);
      loadBanner();
    } finally {
      setClicked(false);
    }
  };

  return (
    <div style={{ padding: '0px 50px 0px 50px' }}>
      <Loading show={bannerState?.fetch} />

      <DialogTitle id="simple-dialog-title">
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
          <div>Banner Slides</div>
          <div>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleSubmit}
              disabled={clicked}
              className={classes.activeButton}
            >
              Save
            </Button>
          </div>
        </div>
      </DialogTitle>

      <br />

      <Box style={{ border: '1px solid lightgray', borderRadius: '10px' }}>
        <Grid container spacing={2} style={{ padding: '2vw' }}>
          <div style={{ width: '100%' }}>
            <DialogTitle id="banner-slides-title">
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <div>Edit Banner Container</div>
              </div>
            </DialogTitle>

            <div style={{ padding: '2vw' }}>
              <Grid>
                <FormControl
                  style={{ marginTop: '0px', width: '20vw' }}
                  fullWidth
                  margin="normal"
                  error={placementTitleError !== ''}
                >
                  <InputLabel htmlFor="placement-title-input">Placement Title</InputLabel>
                  <Input
                    id="placement-title-input"
                    type="text"
                    value={placementTitle}
                    onChange={(e) => { setPlacementTitle(e.target.value); setPlacementTitleError(''); }}
                  />
                  <FormHelperText>{placementTitleError}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid>
                <FormControl
                  style={{ marginTop: '0px', width: '20vw' }}
                  fullWidth
                  margin="normal"
                  error={placementDescError !== ''}
                >
                  <InputLabel htmlFor="placement-desc-input">Placement Description</InputLabel>
                  <Input
                    id="placement-desc-input"
                    type="text"
                    value={placementDesc}
                    onChange={(e) => { setPlacementDesc(e.target.value); setPlacementDescError(''); }}
                    multiline
                    rows={3}
                  />
                  <FormHelperText>{placementDescError}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid style={{ fontSize: '0.9vw', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <div>Slides</div>
                <IconButton onClick={addSlide} style={{ marginLeft: '1vw' }}>
                  <AddIcon />
                </IconButton>
              </Grid>

              {slides.map((element, index) => (
                <Accordion key={index} aria-controls="panel1a-content" id="panel1a-header">
                  <AccordionSummary>
                    <Typography>
                      {getSlideSortKey(index)} {element.description}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid key={index} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                      <Grid style={{ border: '1px solid black', borderRadius: '1vw', width: 'fit-content', padding: '1vw', marginTop: '1vw' }}>

                        <Grid style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                          <FormControl style={{ marginTop: '0px', width: '20vw' }} margin="normal" error={slidesError[index]?.[0] !== ''}>
                            <InputLabel>Slide Description</InputLabel>
                            <Input
                              type="text"
                              value={element.description}
                              onChange={(e) => handleSlideDesc(e.target.value, index)}
                              multiline
                              rows={3}
                            />
                            <FormHelperText>{slidesError[index]?.[0]}</FormHelperText>
                          </FormControl>

                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'end' }}>
                            <FormControl style={{ marginTop: '0px', width: '7vw', paddingTop: '1vw' }} margin="normal">
                              <InputLabel id={`order-label-${index}`}>Order</InputLabel>
                              <Select
                                labelId={`order-label-${index}`}
                                value={getSlideSortKey(index)}
                                label="Order"
                                onChange={(e) => handleOrderedSlides(Number(e.target.value), index)}
                                variant="standard"
                                disabled={disableOrderSelect(index)}
                              >
                                {slides.map((_, i) => (
                                  <MenuItem key={i} value={i + 1}>{i + 1}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <Typography variant="caption" style={{ color: 'red' }}>
                              {disableOrderSelect(index) ? 'Please save before changing order' : ''}
                            </Typography>
                          </div>
                        </Grid>

                        <Grid>
                          <FormControlLabel
                            style={{ marginLeft: 'unset' }}
                            control={
                              <Checkbox
                                checked={element.hide_description}
                                onChange={() => handleHideDescription(index)}
                                name="hideDescriptionCheckbox"
                                color="primary"
                              />
                            }
                            label="Hide Description"
                          />
                        </Grid>

                        <Grid>
                          <FormControl style={{ marginTop: '25px', width: '20vw' }} fullWidth margin="normal" error={slidesError[index]?.[3] !== ''}>
                            <InputLabel>External URL</InputLabel>
                            <Input
                              type="text"
                              value={element.external_url}
                              onChange={(e) => handleSlideExternalURL(e.target.value, index)}
                            />
                            <FormHelperText>{slidesError[index]?.[3]}</FormHelperText>
                          </FormControl>
                        </Grid>

                        <Grid style={{ display: 'flex', flexDirection: 'row' }}>
                          <FormControl style={{ marginTop: '0px', marginBottom: '2vw', width: '20vw' }} fullWidth margin="normal" error={productError[index] !== ''} required>
                            <Autocomplete
                              value={product[index]}
                              onChange={(_, newValue) => handleChangeProduct(newValue, index)}
                              inputValue={inputValue[index] || ''}
                              onInputChange={(_, newInputValue) => {
                                const x = [...inputValue]; x[index] = newInputValue; setInputValue(x);
                              }}
                              options={allEditions}
                              getOptionLabel={(option) => (option.productName && option.id !== 0) ? option.productName : ''}
                              renderOption={(option) => option.productName || ''}
                              loading={allEditions.length === 0}
                              loadingText="Loading products"
                              renderInput={(params) => <TextField {...params} label="Artwork" />}
                            />
                            <FormHelperText>{productError[index]}</FormHelperText>
                          </FormControl>
                          <FormControl style={{ marginTop: '0px', marginLeft: '2vw', width: '17.5vw' }} fullWidth margin="normal">
                            <InputLabel>Value</InputLabel>
                            <Input type="text" value={product[index]?.value || ''} disabled />
                          </FormControl>
                        </Grid>

                        <Grid style={{ display: 'flex', justifyContent: 'center', marginTop: '1vw', flexDirection: 'column' }}>
                          <ThemeProvider theme={dropzoneTheme}>
                            <DropzoneArea
                              acceptedFiles={['image/*']}
                              dropzoneText="Drag and drop the Slide image here"
                              showPreviewsInDropzone
                              onChange={(files) => handleImage(files, index)}
                              filesLimit={0}
                            />
                            <Typography variant="caption" style={{ color: '#fe309c' }}>
                              For better resolution insert images with 2500x1250
                            </Typography>
                            <br />
                          </ThemeProvider>
                          <div style={{ display: 'flex', flexDirection: 'row' }}>
                            <div style={{ display: previewImage[index] ? 'none' : 'flex', flexDirection: 'column', maxWidth: '3vw' }}>
                              <img src={imagePaths[index]} style={{ width: '3vw', height: '3vw', marginTop: '1vw' }} />
                            </div>
                          </div>
                          <FormHelperText style={{ color: 'red' }}>{slidesError[index]?.[1]}</FormHelperText>
                        </Grid>

                        <Grid style={{ display: 'flex', justifyContent: 'center', marginTop: '1vw', flexDirection: 'column' }}>
                          <ThemeProvider theme={dropzoneTheme}>
                            <DropzoneArea
                              acceptedFiles={['image/*']}
                              dropzoneText="Drag and drop the Slide image portrait here"
                              showPreviewsInDropzone
                              onChange={(files) => handleImagePortrait(files, index)}
                              filesLimit={0}
                            />
                            <Typography variant="caption" style={{ color: '#fe309c' }}>
                              For better resolution insert images with 1080x1920
                            </Typography>
                            <br />
                          </ThemeProvider>
                          <div style={{ display: 'flex', flexDirection: 'row' }}>
                            <div style={{ display: previewImagePortrait[index] ? 'none' : 'flex', flexDirection: 'column', maxWidth: '3vw' }}>
                              <img src={imagePathsPortrait[index]} style={{ width: '3vw', height: '3vw', marginTop: '1vw' }} />
                            </div>
                          </div>
                          <FormHelperText style={{ color: 'red' }}>{slidesError[index]?.[5]}</FormHelperText>
                        </Grid>

                        <Grid style={{ display: 'flex', justifyContent: 'center', marginTop: '1vw', flexDirection: 'column' }}>
                          <ThemeProvider theme={dropzoneTheme}>
                            <DropzoneArea
                              acceptedFiles={['video/*']}
                              dropzoneText="Drag and drop the Slide video here"
                              showPreviewsInDropzone
                              onChange={(files) => handleVideo(files, index)}
                              filesLimit={0}
                            />
                          </ThemeProvider>
                          <div style={{ display: slides[index]?.video !== '' ? 'flex' : 'none', flexDirection: 'row' }}>
                            <div style={{ display: previewVideo[index] ? 'none' : 'flex', flexDirection: 'column', maxWidth: '3vw' }}>
                              <IconButton>
                                <VideoCallIcon onClick={() => window.open(slides[index].video as string, '_blank')} />
                              </IconButton>
                            </div>
                          </div>
                          <FormHelperText style={{ color: 'red' }}>{slidesError[index]?.[2]}</FormHelperText>
                        </Grid>

                      </Grid>

                      <Grid>
                        <IconButton disabled={index === 0} onClick={() => deleteSlideLocal(index)}>
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
            </div>
          </div>
        </Grid>
      </Box>
      <br />
    </div>
  );
}
